import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { FOUNDER_IMG } from "../constants";
import { useLang } from "../i18n";

const fadeUp = {
  hidden: { opacity: 0, y: 44 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.12, duration: 0.95, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Founder() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  return (
    <section ref={ref} className="px-6 pb-28 md:px-12 md:pb-40" data-testid="founder">
      <div className="grid items-center gap-16 lg:grid-cols-12">
        <div className="relative order-2 lg:order-1 lg:col-span-5">
          <div className="absolute -inset-3 border border-gold/25 clip-frame" />
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            className="clip-frame relative overflow-hidden"
            style={{ boxShadow: "0 40px 90px rgba(0,0,0,0.6)" }}
            data-testid="founder-portrait"
          >
            <motion.img src={FOUNDER_IMG} alt="Marco Colazzo — Founder" className="h-full w-full object-cover" style={{ y, scale: 1.1 }} />
            <div className="absolute inset-0 bg-[radial-gradient(80%_60%_at_50%_40%,transparent,rgba(10,8,8,0.35))]" />
          </motion.div>
        </div>

        <div className="order-1 lg:order-2 lg:col-span-7">
          <motion.p
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            className="text-[0.65rem] uppercase tracking-[0.5em] text-gold"
          >
            {t("founder.eyebrow")}
          </motion.p>
          <motion.h2
            variants={fadeUp}
            custom={1}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            className="font-display text-carved-ivory mt-6 text-4xl leading-tight md:text-6xl"
          >
            Marco Colazzo
          </motion.h2>
          <motion.p
            variants={fadeUp}
            custom={2}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            className="mt-4 text-[0.7rem] uppercase tracking-[0.4em] text-gold"
          >
            {t("founder.role")}
          </motion.p>
          <motion.p
            variants={fadeUp}
            custom={3}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            className="text-carved-gold font-display mt-10 max-w-2xl text-lg leading-relaxed md:text-xl"
          >
            {t("founder.line")}
          </motion.p>
          <motion.p
            variants={fadeUp}
            custom={4}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            className="text-carved-gold font-display mt-6 max-w-2xl text-lg leading-relaxed md:text-xl"
          >
            {t("founder.line2")}
          </motion.p>
        </div>
      </div>
    </section>
  );
}
